/**
 * GuestNameModal - Modal that asks guest users for their name and phone immediately after login
 * Phone is required so the barber can contact the client.
 */

import { useState } from 'react';
import { User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface GuestNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, phone: string) => Promise<void>;
}

const PHONE_REGEX = /^[+\d][\d\s-]{7,}$/;

export function GuestNameModal({
  isOpen,
  onSubmit,
}: GuestNameModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName) {
      setError(t('guestNameModal.errorEmpty', 'Por favor, introduce tu nombre'));
      return;
    }

    if (trimmedName.length < 2) {
      setError(t('guestNameModal.errorTooShort', 'El nombre debe tener al menos 2 caracteres'));
      return;
    }

    if (trimmedName.length > 30) {
      setError(t('guestNameModal.errorTooLong', 'El nombre no puede tener más de 30 caracteres'));
      return;
    }

    if (!trimmedPhone) {
      setError(t('guestNameModal.errorPhoneEmpty', 'Por favor, introduce tu teléfono'));
      return;
    }

    if (!PHONE_REGEX.test(trimmedPhone)) {
      setError(t('guestNameModal.errorPhoneInvalid', 'Introduce un teléfono válido'));
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await onSubmit(trimmedName, trimmedPhone);
    } catch {
      setError(t('guestNameModal.errorSaving', 'Error al guardar los datos'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-name-modal-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-scale-in"
      >
        {/* Header with gradient background */}
        <div className="relative p-6 text-center">
          {/* User icon */}
          <div className="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-4">
            <User size={40} className="text-accent" />
          </div>

          {/* Title */}
          <h2 id="guest-name-modal-title" className="text-2xl font-bold text-white mb-2">
            {t('guestNameModal.title', '¿Cómo te llamas?')}
          </h2>
          <p className="text-accent text-sm">
            {t('guestNameModal.subtitleWithPhone', 'Necesitamos tu nombre y teléfono para avisarte')}
          </p>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Name input */}
          <div className="mb-3">
            <label htmlFor="guest-name" className="sr-only">
              {t('guestNameModal.nameLabel', 'Tu nombre')}
            </label>
            <input
              id="guest-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder={t('guestNameModal.namePlaceholder', 'Escribe tu nombre...')}
              className="w-full px-4 py-3 border border-hairline rounded-xl focus:ring-2 focus:ring-accent focus:border-transparent bg-surface-3 text-ink text-center text-lg"
              autoFocus
              autoComplete="name"
              maxLength={30}
            />
          </div>

          {/* Phone input */}
          <div className="mb-4">
            <label htmlFor="guest-phone" className="sr-only">
              {t('guestNameModal.phoneLabel', 'Tu teléfono')}
            </label>
            <input
              id="guest-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setError('');
              }}
              placeholder={t('guestNameModal.phonePlaceholder', 'Teléfono (ej: 612345678)')}
              className="w-full px-4 py-3 border border-hairline rounded-xl focus:ring-2 focus:ring-accent focus:border-transparent bg-surface-3 text-ink text-center text-lg"
              autoComplete="tel"
              inputMode="tel"
              maxLength={20}
            />
            {error && (
              <p className="mt-2 text-sm text-danger text-center">
                {error}
              </p>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || !name.trim() || !phone.trim()}
            className="w-full py-3 px-6 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? t('common.saving', 'Guardando...')
              : t('guestNameModal.submit', 'Continuar')}
          </button>
        </form>
      </div>
    </div>
  );
}
